import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useReveal from '@/hooks/useReveal';
import { fetchProjects, normalizeProjectLink, type Project } from '@/lib/api';

const baseCategories = ['All', 'Websites', 'AI', 'E-commerce', 'Marketing', 'Automation', 'Other'];

export default function PortfolioPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useReveal([projects, filter]);

  useEffect(() => {
    let isMounted = true;
    const loadData = async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      try {
        const data = await fetchProjects();
        if (isMounted) {
          setProjects(data);
        }
      } catch (e) {
        console.error('Failed to load portfolio projects:', e);
      } finally {
        if (isMounted && showSpinner) setLoading(false);
      }
    };

    loadData(true);

    const handleProjectsUpdated = () => {
      loadData(false);
    };

    window.addEventListener('projects_updated', handleProjectsUpdated);
    window.addEventListener('storage', handleProjectsUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener('projects_updated', handleProjectsUpdated);
      window.removeEventListener('storage', handleProjectsUpdated);
    };
  }, []);

  const categories = useMemo(() => {
    const list = [...baseCategories];
    projects.forEach((p) => {
      if (p.category && p.category.trim()) {
        const cat = p.category.trim();
        if (!list.some((c) => c.toLowerCase() === cat.toLowerCase())) {
          list.push(cat);
        }
      }
    });
    return list;
  }, [projects]);

  const filtered = filter === 'All'
    ? projects
    : projects.filter((p) => (p.category || 'Other').trim().toLowerCase() === filter.toLowerCase());

  return (
    <div className="bg-ink-950 text-white min-h-screen">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative pt-[clamp(105px,8.5vw,135px)] pb-12 sm:pb-16 overflow-hidden w-full">
        <img
          src="https://readdy.ai/api/search-image?query=3D%20rendered%20abstract%20holographic%20technology%20scene%20with%20floating%203D%20geometric%20shapes%20and%20glowing%20purple%20neon%20wireframe%20elements%20in%20deep%20dark%20space%2C%20soft%20violet%20and%20cyan%20haze%2C%20glossy%20isometric%20render%20style%2C%20floating%20holographic%20interface%20elements%2C%20premium%20cinematic%20atmosphere&width=1800&height=1200&seq=fc-pf2-hero&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 w-full">
          <div className="max-w-3xl">
            <div className="text-xs font-mono tracking-widest text-neon-400">// PORTFOLIO & CASE STUDIES</div>
            <h1 className="mt-4 font-display text-4xl sm:text-6xl md:text-7xl font-bold leading-[1.08] tracking-tight text-gradient">
              Our Featured Projects
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              Explore our portfolio of intelligent web applications, AI automation systems, digital platforms, and growth engineering case studies.
            </p>
          </div>
        </div>
      </section>

      {/* PORTFOLIO GRID & CATEGORIES */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl">
          {/* Category Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-8 sm:mb-12 reveal">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition border ${
                  filter === c
                    ? 'bg-neon-500 text-white border-neon-500 shadow-[0_0_15px_rgba(139,92,246,0.4)]'
                    : 'bg-transparent text-white/60 border-white/10 hover:border-neon-500/50 hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-20 text-center text-white/50 text-sm">
              <i className="ri-loader-4-line animate-spin mr-2 text-neon-400 text-xl" />
              Loading portfolio items...
            </div>
          ) : filtered.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center border border-white/10 max-w-md mx-auto">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-neon-500/10 text-neon-400 text-2xl mb-4">
                <i className="ri-briefcase-4-line" />
              </div>
              <h3 className="font-display font-semibold text-white text-lg">No projects found</h3>
              <p className="mt-2 text-sm text-white/50">There are no projects in the "{filter}" category yet.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item) => {
                const formattedLink = normalizeProjectLink(item.link);
                const isExternalLink = formattedLink.startsWith('http://') || formattedLink.startsWith('https://');
                const isInternalLink = formattedLink.startsWith('/');

                return (
                  <div
                    key={item.id || item.name}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] hover:border-neon-500/50 hover:bg-white/[0.04] transition-all duration-500 flex flex-col"
                  >
                    {/* Thumbnail Container */}
                    <div className="aspect-[4/3] overflow-hidden relative bg-ink-900">
                      <img
                        src={item.image || 'https://readdy.ai/api/search-image?query=3D%20rendered%20holographic%20abstract%20technology%20shape%20floating%20in%20dark%20purple%20space%20with%20glowing%20neon%20accents&width=900&height=600&seq=fc-pf-default&orientation=landscape'}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
                      
                      {item.featured && (
                        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-neon-500/20 border border-neon-500/40 text-neon-300 text-[10px] font-semibold tracking-wider uppercase backdrop-blur">
                          Featured
                        </div>
                      )}
                    </div>

                    {/* Content Box */}
                    <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[11px] text-neon-300 font-mono tracking-widest uppercase">
                            {item.category || 'Websites'}
                          </span>
                        </div>

                        <h3 className="font-display font-bold text-lg sm:text-xl text-white group-hover:text-neon-300 transition-colors">
                          {item.name}
                        </h3>

                        {item.description && (
                          <p className="mt-2 text-xs sm:text-sm text-white/60 leading-relaxed line-clamp-3">
                            {item.description}
                          </p>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                        {/* Tech Tags */}
                        {item.tech && item.tech.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {item.tech.map((t) => (
                              <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/50">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Project Link Action */}
                        <div>
                          {isExternalLink ? (
                            <a
                              href={formattedLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-neon-400 hover:text-neon-300 transition-all uppercase tracking-wider group-hover:translate-x-1"
                            >
                              Visit Live Project <i className="ri-arrow-right-up-line text-sm" />
                            </a>
                          ) : isInternalLink ? (
                            <a
                              href={formattedLink}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-neon-400 hover:text-neon-300 transition-all uppercase tracking-wider group-hover:translate-x-1"
                            >
                              View Project Link <i className="ri-arrow-right-line text-sm" />
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider">
                              Internal Case Study
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}